/**
 * CategoryButton View Component
 *
 * Pure view component for category button displaying recommendation type.
 * @module components/PlayerDetailRight/CategoryButton/CategoryButtonView
 */

import { memo } from "react";

import {
  getCategoryButtonClasses,
  getCategoryTextClasses,
} from "@/utils/playerDetailRight";

import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { IRecommendationsCategorySelection } from "@/stores/recommendationsCategorySelection";

/**
 * Props for CategoryButtonView component
 */
interface CategoryButtonViewProps {
  readonly item: IRecommendationsCategorySelection;
  readonly isSelected: boolean;
  readonly isDark: boolean;
  readonly isSmallScreen: boolean;
  readonly onPress: () => void;
}

/**
 * Pure view component for category button
 * Displays a pressable button with styling based on selection state.
 * @param root0 - Component props
 * @param root0.item - Category item containing type and label
 * @param root0.isSelected - Whether this category is currently selected
 * @param root0.isDark - Whether dark theme is active
 * @param root0.isSmallScreen - Whether device has small screen
 * @param root0.onPress - Handler called when button is pressed
 */
const CategoryButtonView = ({
  item,
  isSelected,
  isDark,
  isSmallScreen,
  onPress,
}: CategoryButtonViewProps) => (
  <Pressable
    className={getCategoryButtonClasses({
      isSelected,
      isDark,
    })}
    onPress={onPress}
  >
    <Text
      variant="unstyled"
      className={getCategoryTextClasses({
        isSelected,
        isDark,
        isSmallScreen,
      })}
    >
      {item.label}
    </Text>
  </Pressable>
);

CategoryButtonView.displayName = "CategoryButtonView";

export default memo(CategoryButtonView);
